import React, { useState } from "react";
import { Divider, CircularProgress } from "@mui/material";
import CommonButtons from "src/shared/components/CommonButtons";
import PaymentHeader from "./../components/paymentSection/PaymentHeader";
import TotalAmountCard from "./../components/paymentSection/TotalAmountCard";
import TipSelector from "./../components/paymentSection/TipSelector";
import PaymentMethodSelector, { SplitPayment } from "./../components/paymentSection/PaymentMethodSelector";
import OrderSummaryCard from "./../components/paymentSection/OrderSummaryCard";
import PaymentCompletedCard from "./../components/paymentSection/PaymentCompletedCard";
import { usePaymentTheme } from "../hooks/usePaymentTheme";
import PaymentIcon from "@mui/icons-material/Payment";
import { Order } from "src/shared/services/interfacev2";

interface Props {
  order: Order | null;
  tip: number;
  customTip: string;
  paymentMethod: string;
  guess: number;
  total: number;
  onTipChange: (amount: number) => void;
  onCustomTipChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPayNow: () => void;
  onPaymentMethodChange: (method: string) => void;
  isProcessing: boolean;
  onConfirmEdit: (data: { parcelFee: number; deliveryFee: number; discount: number }) => void;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (value: boolean) => void;
  onFeeChange?: (fees: { parcelFee: number; deliveryFee: number; discount: number }) => void;
  splitPayment: SplitPayment;
  onSplitPaymentChange: (split: SplitPayment) => void;
}

const PaymentDetailsSection: React.FC<Props> = ({
  order,
  tip,
  customTip,
  paymentMethod,
  guess,
  total,
  onTipChange,
  onCustomTipChange,
  onPayNow,
  onPaymentMethodChange,
  isProcessing,
  onConfirmEdit,
  hasUnsavedChanges,
  setHasUnsavedChanges,
  onFeeChange,
  splitPayment,
  onSplitPaymentChange,
}) => {
  const { fontClass, themeBorderColor, themeTextColor, themeRingColor, getBgClass, getLightBgClass, sidebarThemeClass } = usePaymentTheme();
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  const isCompleted = order?.orders[0].status.toLowerCase() === "completed";

  // Calculate if split is invalid
  const splitSum = Number((splitPayment.cashAmount + splitPayment.cardAmount).toFixed(2));
  const isSplitInvalid = paymentMethod === "Split" && splitSum !== Number(total.toFixed(2));

  // Also consider split invalid if both amounts are zero (meaning user hasn't entered anything yet)
  const isSplitEmpty = paymentMethod === "Split" && splitPayment.cashAmount === 0 && splitPayment.cardAmount === 0;

  const isButtonEnabled = 
    !!paymentMethod && 
    !isProcessing && 
    !isButtonDisabled && 
    !isCompleted && 
    !hasUnsavedChanges && 
    !isSplitInvalid && 
    !isSplitEmpty;

  const finalAmount = (Number(order?.orders[0].finalAmount ?? 0) + tip).toFixed(2);

  const handlePayNow = async () => {
    setIsButtonDisabled(true);
    try {
      await onPayNow();
    } catch (error) {
      setIsButtonDisabled(false);
    }
  };

  return (
    <div className={`rounded-xl shadow-lg border border-gray-100 p-1.5 flex flex-col ${sidebarThemeClass} ${fontClass}`}>
      <PaymentHeader getBgClass={getBgClass} themeTextColor={themeTextColor} />
      <TotalAmountCard order={order} guess={guess} getBgClass={getBgClass} isCompleted={isCompleted} finalAmount={finalAmount} />

      <Divider className="my-4 border-gray-200" />
      <TipSelector
        tip={tip}
        customTip={customTip}
        isCompleted={isCompleted}
        onTipChange={onTipChange}
        onCustomTipChange={onCustomTipChange}
        themeBorderColor={themeBorderColor}
        themeTextColor={themeTextColor}
        themeRingColor={themeRingColor}
        getLightBgClass={getLightBgClass}
      />

      <Divider className="my-4 border-gray-200" />
      <PaymentMethodSelector
        paymentMethod={paymentMethod}
        isCompleted={isCompleted}
        onPaymentMethodChange={onPaymentMethodChange}
        themeBorderColor={themeBorderColor}
        themeTextColor={themeTextColor}
        getLightBgClass={getLightBgClass}
        total={total}
        splitPayment={splitPayment}
        onSplitPaymentChange={onSplitPaymentChange}
      />

      <Divider className="my-4 mb-0.5 border-gray-200" />
      <OrderSummaryCard
        order={order}
        tip={tip}
        total={total}
        themeTextColor={themeTextColor}
        getLightBgClass={getLightBgClass}
        onConfirmEdit={onConfirmEdit}
        setHasUnsavedChanges={setHasUnsavedChanges}
        onFeeChange={onFeeChange}
      />

      {isCompleted ? (
        <PaymentCompletedCard />
      ) : (
        <CommonButtons
          label={
            isProcessing ? (
              <div className="flex items-center gap-3">
                <CircularProgress size={24} className="text-white" /> Processing...
              </div>
            ) : paymentMethod === "Split" && isSplitEmpty ? (
              "ENTER SPLIT AMOUNTS"
            ) : paymentMethod === "Split" && isSplitInvalid ? (
              "SPLIT AMOUNT MISMATCH"
            ) : paymentMethod ? (
              `PAY ${total.toFixed(2)}`
            ) : (
              "SELECT PAYMENT METHOD"
            )
          }
          className="h-12"
          icon={PaymentIcon}
          onClick={handlePayNow}
          disabled={!isButtonEnabled}
        />
      )}
    </div>
  );
};

export default PaymentDetailsSection;